<template>
  <div>
    <keep-alive>
      <OrderBarrelDetails
        v-if="activeScreen == this.getPageIndex(pageMap.ORDERPAGE.name)"
        v-model="order"
        :formProgress="formProgress"
        @update:additionalCosts="additionalCosts = $event"
        @next="activeScreen++"
      />
      <OrderBarrelConsignee
        v-if="activeScreen == this.getPageIndex(pageMap.CONSIGNEE.name)"
        v-model="consignee"
        :finalScreen="this.isFinalScreen(pageMap.CONSIGNEE.name)"
        :formProgress="formProgress"
        :loading="placeOrderLoading"
        @next="activeScreen++"
        @previous="activeScreen--"
        @pay="makeOrderConfirmation"
      />
      <OrderBarrelSummary
        v-if="activeScreen == this.getPageIndex(pageMap.ORDERSUMMARY.name)"
        :order="order"
        :additionalCosts="additionalCosts"
        :placeOrderLoading="placeOrderLoading"
        :saveOrderLoading="saveOrderLoading"
        :consignee="consignee"
        :finalScreen="this.isFinalScreen(pageMap.ORDERSUMMARY.name)"
        :formProgress="formProgress"
        @update:paymentConfig="paymentConfig = $event"
        @previous="activeScreen--"
        @pay="makeOrderConfirmation"
        @save="saveOrder"
        @show:notification="showNotification"
        @next="activeScreen++"
      />
    </keep-alive>
    <ConfirmationModal
      :isLoadingConfirmation="loadingConfirmation"
      :isShowDialog="isShowConfirmationModal"
      :btnConfig="buttonConfiguration"
      :inputInfo="confirmationModalInfo"
      :inputColor="confirmationModalInfoColor"
      @eConfirm="makeOrder"
      @eCancel="defaultCancelOperation"
    />
    <SnackbarNotifier
      :isShow="isShowSnackBar"
      :message="responseMessage"
      :snackType="snackbarType"
      :colour="snackBarColour"
      @eReset="resetSnackBar"
    />
    <AuthenticationDialog
      v-bind="authenticationDialogData"
      @dismiss="handleAuthenticationDialogResponse"
    />
  </div>
</template>

<script>
import { ApiService, PaymentOption } from "@/services/ApiService";
import { useStore } from "@/store";
import { error } from "@/services/LoggerService";
import OrderBarrelDetails from "@/components/OrderBarrelDetails.vue";
import OrderBarrelConsignee from "@/components/OrderBarrelConsignee.vue";
import OrderBarrelSummary from "@/components/OrderBarrelSummary.vue";
import ConfirmationModal from "@/components/ConfirmationModal.vue";
import ConfirmationModalMixin from "@/mixins/ConfirmationModalMixins";
import SnackbarNotifier from "@/components/SnackbarNotifier.vue";
import SnackbarNotifierMixin from "@/mixins/SnackbarNotifierMixins";
import { AuthenticationMixin } from "@/components/Authentication.vue";
import mixins from "vue-typed-mixins";
import Appsettings from "@/config/appsettings.json";

export default mixins(
  AuthenticationMixin,
  ConfirmationModalMixin,
  SnackbarNotifierMixin
).extend({
  name: "OrderBarrel",
  components: {
    OrderBarrelDetails,
    OrderBarrelConsignee,
    OrderBarrelSummary,
    ConfirmationModal,
    SnackbarNotifier,
  },
  data() {
    return {
      placeOrderLoading: false,
      saveOrderLoading: false,
      pageMap: {
        ORDERPAGE: {
          screen: OrderBarrelDetails,
          label: "CUSTOMIZE",
          name: "ORDERPAGE",
        },
        CONSIGNEE: {
          screen: OrderBarrelConsignee,
          label: "RECIPIENT",
          name: "CONSIGNEE",
        },
        ORDERSUMMARY: {
          screen: OrderBarrelSummary,
          label: "CONFIRM",
          name: "ORDERSUMMARY",
        },
      },
      order: {
        deliveryAddress: {},
        removeSecondaryPackaging: true,
        removeRetailPackaging: null,
        capacity: "",
        barrelSize: "",
        shippingCost: 0,
        barrelCost: 0,
        deliveryCost: 0,
        deliveryMethod: "delivery",
      },
      barrelId: "",
      consignee: {
        firstName: "",
        lastName: "",
        email: "",
        trn: "",
        contactNumber: "",
        address: {
          line1: "",
          line2: "",
          town: "",
          parish: "",
        },
      },
      additionalCosts: {
        clearanceCost: 0,
        deliveryGctPercentage: 0,
      },
      paymentConfig: {
        promoCode: {},
        paymentMethod: PaymentOption.UPFRONT,
      },
      terms: false,
      screens: [
        { screen: OrderBarrelDetails },
        { screen: OrderBarrelConsignee },
        { screen: OrderBarrelSummary },
      ],
      activeScreen: 0,
      orderBarrelErrorMap: {
        default: "Failed To Place An Order",
        "You already have an active barrel":
          "Cannot have more than 1 open barrel",
        "Promo code already used": "Promo code already used",
      },
    };
  },
  computed: {
    isLoggedIn() {
      const store = useStore();
      return store.getters.isAuthenticated;
    },
    pageOrder() {
      if (this.isLoggedIn) {
        return [
          this.pageMap.ORDERPAGE.name,
          this.pageMap.CONSIGNEE.name,
          this.pageMap.ORDERSUMMARY.name,
        ];
      }
      return [
        this.pageMap.ORDERPAGE.name,
        this.pageMap.ORDERSUMMARY.name,
        this.pageMap.CONSIGNEE.name,
      ];
    },
    formProgress() {
      return this.pageOrder.map((p, index) => {
        return {
          isActive: index <= this.activeScreen,
          label: this.pageMap[p].label,
        };
      });
    },
    disableWarehouse() {
      return Appsettings.DisableWarehouse ?? false;
    },
    allowedCustomerBuyException() {
      try {
        const groups = this.$store.getters.getGroups ?? [];
        return groups.includes("CustomerAllowedToBuy");
      } catch (e) {}

      return false;
    },
  },
  methods: {
    getPageIndex(page) {
      return this.pageOrder.findIndex((e) => e === page);
    },
    isFinalScreen(page) {
      return this.pageOrder.length == this.getPageIndex(page) + 1;
    },
    async makeOrderConfirmation() {
      try {
        if (!(await this.authenticateUser())) {
          return;
        }
      } catch (e) {
        error(e);
        return;
      }
      this.showConfirmationModal({
        title: "Order Barrel",
        message: "Are you sure you want to order this barrel?",
      });
    },
    async makeOrder() {
      try {
        this.placeOrderLoading = true;
        this.startConfirmationLoading();
        this.order.barrelHash = this.getBarrelHash();
        this.order.consignee = this.consignee;
        this.order.promoCode = this.paymentConfig.promoCode?.code?.trim();
        this.order.paymentOption = this.paymentConfig.paymentMethod;
        const link = await ApiService.Instance.orderBarrel(this.order);
        if (!link) {
          this.showNotifier({
            message: `Unable To Proceed To Payment Portal`,
            snackbarType: "error",
          });
          return;
        }
        window.location.href = link.paynowproRedirectUrl;
        this.placeOrderLoading = false;
        this.stopConfirmationLoading();
      } catch (e) {
        const errorMessage = this.getOrderBarrelError(e);
        this.showNotifier({
          message: errorMessage,
          snackbarType: "error",
        });

        this.stopConfirmationLoading();
        this.placeOrderLoading = false;
        error(e);
      }
    },
    getOrderBarrelError(e) {
      try {
        const errorMessage =
          this.orderBarrelErrorMap[e.response.data.errorMessage];
        if (errorMessage) {
          return errorMessage;
        }
      } catch (e) {
        error(e);
      }

      return this.orderBarrelErrorMap.default;
    },
    async saveOrder() {
      try {
        if (!(await this.authenticateUser())) {
          return;
        }
        this.saveOrderLoading = true;
        this.startConfirmationLoading();
        this.order.barrelHash = this.getBarrelHash();
        if (this.consignee?.email != null) {
          this.order.consignee = this.consignee;
        }
        this.order.promoCode = this.paymentConfig.promoCode?.code;
        this.order.paymentOption = this.paymentConfig.paymentMethod;
        await ApiService.Instance.saveBarrelSelection(this.order);
        this.showNotifier({
          message: `Order Saved`,
          snackbarType: "success",
        });
        this.saveOrderLoading = false;
        this.stopConfirmationLoading();
        this.$router.push({
          name: "MyBarrels",
        });
      } catch (e) {
        this.showNotifier({
          message: "Failed To Save The Order",
          snackbarType: "error",
        });
        this.stopConfirmationLoading();
        this.saveOrderLoading = false;
        error(e);
      }
    },
    showNotification({ message, snackbarType }) {
      this.showNotifier({
        message,
        snackbarType,
      });
    },
    getBarrelHash() {
      try {
        if (!this.barrelId) {
          return "";
        }

        return this.barrelId.split("_")[1];
      } catch {}

      return "";
    },
    async fillBarrelDetailsIfAvailable(barrelId) {
      try {
        if (!barrelId) {
          return;
        }

        const result = await ApiService.Instance.barrelDetails({
          logicalBarrelId: barrelId,
        });

        this.order = {
          ...this.order,
          capacity: result.barrel.BarrelCapacity,
          barrelSize: result.barrel.BarrelSize,
          deliveryMethod: result.barrel.DeliveryMethod,
          deliveryAddress: {
            line1: result.barrel.DeliveryAddress.Line1,
            line2: result.barrel.DeliveryAddress.Line2,
            town: result.barrel.DeliveryAddress.Town,
            parish: result.barrel.DeliveryAddress.Parish,
          },
        };

        if (result.barrel.Consignee?.FirstName) {
          this.consignee = {
            ...this.consignee,
            firstName: result.barrel.Consignee.FirstName,
            lastName: result.barrel.Consignee.LastName,
            email: result.barrel.Consignee.Email,
            trn: result.barrel.Consignee.Trn,
            contactNumber: result.barrel.Consignee.ContactNumber,
            address: {
              line1: result.barrel.Consignee.Address.Line1,
              line2: result.barrel.Consignee.Address.Line2,
              town: result.barrel.Consignee.Address.Town,
              parish: result.barrel.Consignee.Address.Parish,
            },
          };
        }

        this.barrelId = barrelId;
      } catch (e) {
        error(e);
      }
    },
  },
  async beforeMount() {
    if (this.disableWarehouse && !this.allowedCustomerBuyException) {
      this.$router.push({ path: "/" });
    }
    this.activeScreen = 0;
    const barrelId = this.$route.params.barrelId;
    await this.fillBarrelDetailsIfAvailable(barrelId);
  },
});
</script>

<style></style>
